import { defineStore } from 'pinia'
import { AnalysisTest } from '@/globals/typescript/models/AnalysisTest'
import type {
  IAnalysisTestAnswer,
  IAnalysisTestProveBasedColorConfig,
  IAnalysisTestSingleSelectColorConfig,
  IAnalysisTestValueBasedColorConfig,
  TAnalysisTest,
} from '@team-milva/lab-models/resources'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { useAccountStore } from '@/stores/accountStore'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'

export { AnalysisTest }

export type TCreateAnalysisTestPayload =
  | {
      name: string
      displayName?: string
      isOptional?: boolean
      type: 'prove-based'
      qaTrigger?: 'always' | 'proved' | 'not-proved' | null
      provedTextID?: string | null
      notProvedTextID?: string | null
      colorConfig?: IAnalysisTestProveBasedColorConfig | null
    }
  | {
      name: string
      displayName?: string
      isOptional?: boolean
      type: 'value-based'
      hasTooLowToMeasure: boolean
      hasTooHighToMeasure: boolean
      minLimit: number
      maxLimit: number
      unitID?: string
      colorConfig?: IAnalysisTestValueBasedColorConfig | null
    }
  | {
      name: string
      displayName?: string
      isOptional?: boolean
      type: 'single-select'
      answers: IAnalysisTestAnswer[]
      colorConfig?: IAnalysisTestSingleSelectColorConfig | null
      qaTrigger?: 'always' | 'answers' | null
      qaAnswerIDs?: string[]
    }
  | {
      name: string
      displayName?: string
      isOptional?: boolean
      type: 'multi-select-text'
      reportDescriptionIDs: string[]
      suffixReportDescriptionIDs: string[]
      answers: IAnalysisTestAnswer[]
      answerMode?: 'none' | 'optional' | 'required'
    }
  | {
      name: string
      displayName?: string
      isOptional?: boolean
      type: 'multi-select-number'
      reportDescriptionIDs: string[]
      suffixReportDescriptionIDs: string[]
      unitID: string
      answerOverrides: IAnalysisTestAnswer[]
      hasSumValue?: boolean
    }

// Full current field set per type — not a diff. Matches the same
// "always send everything" model as the Analysis Focus flow's batched save
// (updateAnalysisService's analysisTests[] entries), since both go through
// the same shared integrity guards on the backend
// (reconcileAnalysisTestUpdate.ts).
export type TUpdateAnalysisTestPayload =
  | {
      name: string
      displayName?: string
      isOptional?: boolean
      type: 'prove-based'
      qaTrigger: 'always' | 'proved' | 'not-proved' | null
      provedTextID?: string | null
      notProvedTextID?: string | null
      colorConfig: IAnalysisTestProveBasedColorConfig | null
    }
  | {
      name: string
      displayName?: string
      isOptional?: boolean
      type: 'value-based'
      hasTooLowToMeasure: boolean
      hasTooHighToMeasure: boolean
      minLimit: number
      maxLimit: number
      unitID: string
      colorConfig: IAnalysisTestValueBasedColorConfig | null
    }
  | {
      name: string
      displayName?: string
      isOptional?: boolean
      type: 'single-select'
      answers: IAnalysisTestAnswer[]
      // Optional: left out, the stored classification is left alone (MIL-550).
      colorConfig?: IAnalysisTestSingleSelectColorConfig | null
      // Optional for the same reason: left out, the stored QA setting is left alone (MIL-551).
      qaTrigger?: 'always' | 'answers' | null
      qaAnswerIDs?: string[]
    }
  | {
      name: string
      displayName?: string
      isOptional?: boolean
      type: 'multi-select-text'
      reportDescriptionIDs: string[]
      suffixReportDescriptionIDs: string[]
      answers: IAnalysisTestAnswer[]
      answerMode: 'none' | 'optional' | 'required'
    }
  | {
      name: string
      displayName?: string
      isOptional?: boolean
      type: 'multi-select-number'
      reportDescriptionIDs: string[]
      suffixReportDescriptionIDs: string[]
      unitID: string
      answerOverrides: IAnalysisTestAnswer[]
      hasSumValue: boolean
    }

export const useAnalysisTestStore = defineStore('analysisTest', {
  state: () => ({
    allAnalysisTests: [] as AnalysisTest[],
    unsubscribeAllAnalysisTests: null as (() => void) | null,
  }),

  actions: {
    getAnalysisTests(): Promise<AnalysisTest[]> {
      if (this.allAnalysisTestsLoaded) {
        return Promise.resolve(this.allAnalysisTests)
      }

      return new Promise((resolve) => {
        let resolved = false

        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'AnalysisTests'),
          where('accountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allAnalysisTests = querySnapshot.docs.map(
            (doc) =>
              new AnalysisTest({ ...doc.data(), id: doc.id } as TAnalysisTest)
          )
          this.unsubscribeAllAnalysisTests = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(this.allAnalysisTests)
          }
        })
      })
    },

    // Goes through lab-cloud (createAnalysisTestService) rather than a direct
    // client Firestore write — AnalysisTest creation has no other backend path
    // yet, and client writes are meant to be blocked by production Firestore
    // rules (see Batch 1's equivalent fix for updates in updateAnalysisService).
    async createAnalysisTest(
      payload: TCreateAnalysisTestPayload
    ): Promise<string> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const body =
        payload.type === 'prove-based'
          ? {
              name: payload.name,
              displayName: payload.displayName,
              isOptional: payload.isOptional ?? false,
              type: payload.type,
              qaTrigger: payload.qaTrigger ?? null,
              provedTextID: payload.provedTextID ?? null,
              notProvedTextID: payload.notProvedTextID ?? null,
              colorConfig: payload.colorConfig ?? null,
            }
          : payload.type === 'value-based'
            ? {
                name: payload.name,
                displayName: payload.displayName,
                isOptional: payload.isOptional ?? false,
                type: payload.type,
                hasTooLowToMeasure: payload.hasTooLowToMeasure,
                hasTooHighToMeasure: payload.hasTooHighToMeasure,
                minLimit: payload.minLimit,
                maxLimit: payload.maxLimit,
                unitID: payload.unitID ?? '',
                colorConfig: payload.colorConfig ?? null,
              }
            : payload.type === 'single-select'
              ? {
                  name: payload.name,
                  displayName: payload.displayName,
                  isOptional: payload.isOptional ?? false,
                  type: payload.type,
                  answers: payload.answers,
                  colorConfig: payload.colorConfig ?? null,
                  qaTrigger: payload.qaTrigger ?? null,
                  qaAnswerIDs: payload.qaAnswerIDs ?? [],
                }
              : payload.type === 'multi-select-text'
                ? {
                    name: payload.name,
                    displayName: payload.displayName,
                    isOptional: payload.isOptional ?? false,
                    type: payload.type,
                    reportDescriptionIDs: payload.reportDescriptionIDs,
                    suffixReportDescriptionIDs: payload.suffixReportDescriptionIDs,
                    answers: payload.answers,
                    answerMode: payload.answerMode ?? 'optional',
                  }
                : {
                    name: payload.name,
                    displayName: payload.displayName,
                    isOptional: payload.isOptional ?? false,
                    type: payload.type,
                    reportDescriptionIDs: payload.reportDescriptionIDs,
                    suffixReportDescriptionIDs: payload.suffixReportDescriptionIDs,
                    unitID: payload.unitID,
                    answerOverrides: payload.answerOverrides,
                    hasSumValue: payload.hasSumValue ?? false,
                  }

      // TODO: remove cast after deploying and re-running openapi:gen
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (managementApi as any)(
        'post',
        'lab-accounts/{labAccountID}/analysis-tests',
        {
          path: { labAccountID },
          body,
        }
      )
      if (!result.success) throw new Error(result.code)
      return result.data.id as string
    },

    // Goes through lab-cloud (updateAnalysisTestService) rather than a direct
    // client Firestore write — this is the same migration createAnalysisTest
    // already made for creation (see its comment above). Sends the full
    // current field set per type, matching the Analysis Focus flow's batched
    // save model, so both save paths share the exact same backend integrity
    // guards (ownership check, type-mismatch rejection, answer-array
    // reconciliation).
    async updateAnalysisTest(
      analysisTestID: string,
      payload: TUpdateAnalysisTestPayload
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const body =
        payload.type === 'prove-based'
          ? {
              name: payload.name,
              displayName: payload.displayName,
              // No `?? false` here, unlike create: an update sends the full
              // current field set, so defaulting a field the caller did not
              // carry through turns "I am not editing this" into "set it to
              // false". JSON.stringify drops an undefined value, and the
              // backend leaves an omitted field alone.
              isOptional: payload.isOptional,
              type: payload.type,
              qaTrigger: payload.qaTrigger,
              provedTextID: payload.provedTextID ?? null,
              notProvedTextID: payload.notProvedTextID ?? null,
              colorConfig: payload.colorConfig,
            }
          : payload.type === 'value-based'
            ? {
                name: payload.name,
                displayName: payload.displayName,
                isOptional: payload.isOptional,
                type: payload.type,
                hasTooLowToMeasure: payload.hasTooLowToMeasure,
                hasTooHighToMeasure: payload.hasTooHighToMeasure,
                minLimit: payload.minLimit,
                maxLimit: payload.maxLimit,
                unitID: payload.unitID,
                colorConfig: payload.colorConfig,
              }
            : payload.type === 'single-select'
              ? {
                  name: payload.name,
                  displayName: payload.displayName,
                  isOptional: payload.isOptional,
                  type: payload.type,
                  answers: payload.answers,
                  // Undefined when the caller does not edit colours: JSON drops it and the
                  // backend leaves the stored classification alone.
                  colorConfig: payload.colorConfig,
                  // Same: undefined when the caller does not edit QA (MIL-551).
                  qaTrigger: payload.qaTrigger,
                  qaAnswerIDs: payload.qaAnswerIDs,
                }
              : payload.type === 'multi-select-text'
                ? {
                    name: payload.name,
                    displayName: payload.displayName,
                    isOptional: payload.isOptional,
                    type: payload.type,
                    reportDescriptionIDs: payload.reportDescriptionIDs,
                    suffixReportDescriptionIDs: payload.suffixReportDescriptionIDs,
                    answers: payload.answers,
                    answerMode: payload.answerMode,
                  }
                : {
                    name: payload.name,
                    displayName: payload.displayName,
                    isOptional: payload.isOptional,
                    type: payload.type,
                    reportDescriptionIDs: payload.reportDescriptionIDs,
                    suffixReportDescriptionIDs: payload.suffixReportDescriptionIDs,
                    unitID: payload.unitID,
                    answerOverrides: payload.answerOverrides,
                    hasSumValue: payload.hasSumValue,
                  }

      // TODO: remove cast after deploying and re-running openapi:gen
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (managementApi as any)(
        'patch',
        'lab-accounts/{labAccountID}/analysis-tests/{analysisTestID}',
        {
          path: { labAccountID, analysisTestID },
          body,
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    resetStore(): void {
      this.allAnalysisTests = []

      if (this.unsubscribeAllAnalysisTests) {
        this.unsubscribeAllAnalysisTests()
        this.unsubscribeAllAnalysisTests = null
      }
    },
  },

  getters: {
    allAnalysisTestsLoaded: (state): boolean =>
      !!state.unsubscribeAllAnalysisTests,
  },
})

